import { AsyncStatus } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import { useCallback, useMemo, useState } from 'react';

import { AwayStatus } from 'apis/presence';
import { AddFavoritesDropdown } from 'components/addFavoritesDropdown';
import { sortPresence } from 'components/menu/utils';
import { PresenceListSection } from 'components/presence/components/presenceListSection';
import { PresenceSectionSkeleton } from 'components/presence/components/presenceSectionSkeleton';
import type { PresenceStateT } from 'components/presence/reducers/presence';

type FavoritesPresenceListSectionProps = {
  presence: PresenceStateT;
  presenceAsyncStatus: AsyncStatus;
  profile: User;
  awayStatuses: AwayStatus[];
  favouriteTeamMemberIds: string[];
};

export const FavoritesPresenceListSection = ({
  presence,
  presenceAsyncStatus,
  profile,
  awayStatuses,
  favouriteTeamMemberIds,
}: FavoritesPresenceListSectionProps): JSX.Element => {
  const { allPresence } = presence;

  // Snapshot of favorites when dropdown opens - prevents list from jumping
  const [frozenFavoriteIds, setFrozenFavoriteIds] = useState<string[] | null>(
    null,
  );

  const handleDropdownOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        setFrozenFavoriteIds(favouriteTeamMemberIds);
      } else {
        setFrozenFavoriteIds(null);
      }
    },
    [favouriteTeamMemberIds],
  );

  const displayedFavoriteIds = frozenFavoriteIds ?? favouriteTeamMemberIds;

  const sortedFavoritePresence = useMemo(() => {
    const me = allPresence.find((p) => p.user.id === profile.id);
    const favorites = allPresence.filter(
      (p) =>
        displayedFavoriteIds.includes(p.user.id) && p.user.id !== profile.id,
    );
    return me ? [me, ...sortPresence(favorites)] : sortPresence(favorites);
  }, [allPresence, displayedFavoriteIds, profile]);

  const isLoading = presenceAsyncStatus === 'loading';

  if (isLoading) {
    return <PresenceSectionSkeleton />;
  }

  return (
    <>
      <PresenceListSection
        key="favorites"
        title={`Favorites (${sortedFavoritePresence.length})`}
        presenceList={sortedFavoritePresence}
        profile={profile}
        ownPresence={presence.presence}
        awayStatuses={awayStatuses}
      />
      <AddFavoritesDropdown onOpenChange={handleDropdownOpenChange} />
    </>
  );
};
